import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faGripVertical, faTrash, faChevronRight,
  faChevronDown, faPlus, faCheck, faSave, faArrowLeft, faStickyNote,
  faExternalLinkAlt, faAtom, faInfoCircle, faListOl, faPen
} from '@fortawesome/free-solid-svg-icons';
import mitreService from '../../services/mitreService';
import atomicService from '../../services/atomicService';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

import {
  faEye, faCogs, faDoorOpen, faBolt, faThumbtack, faLevelUpAlt,
  faShieldAlt as faShieldIcon, faKey, faBinoculars, faExchangeAlt,
  faBoxes, faSatelliteDish, faUpload, faSkullCrossbones
} from '@fortawesome/free-solid-svg-icons';

const tacticIconMap = {
  'Reconnaissance': faEye,
  'Resource Development': faCogs,
  'Initial Access': faDoorOpen,
  'Execution': faBolt,
  'Persistence': faThumbtack,
  'Privilege Escalation': faLevelUpAlt,
  'Defense Evasion': faShieldIcon,
  'Credential Access': faKey,
  'Discovery': faBinoculars,
  'Lateral Movement': faExchangeAlt,
  'Collection': faBoxes,
  'Command and Control': faSatelliteDish,
  'Exfiltration': faUpload,
  'Impact': faSkullCrossbones
};

const tacticColors = {
  'Reconnaissance': 'bg-blue-500',
  'Resource Development': 'bg-indigo-500',
  'Initial Access': 'bg-purple-500',
  'Execution': 'bg-yellow-500',
  'Persistence': 'bg-green-500',
  'Privilege Escalation': 'bg-orange-500',
  'Defense Evasion': 'bg-teal-500',
  'Credential Access': 'bg-red-500',
  'Discovery': 'bg-cyan-500',
  'Lateral Movement': 'bg-pink-500',
  'Collection': 'bg-amber-500',
  'Command and Control': 'bg-violet-500',
  'Exfiltration': 'bg-rose-500',
  'Impact': 'bg-red-700'
};

/**
 * MitrePlaybookBuilder - Drag & drop campaign/playbook builder using MITRE ATT&CK tree
 *
 * @param {string} mode - 'blue' (limited details) or 'red' (full details + atomic tests)
 * @param {Array} initialTechniques - Pre-selected techniques for editing
 * @param {Function} onSave - Callback with { name, objective, description, techniques }
 * @param {Function} onCancel - Cancel callback
 * @param {string} initialName - Initial campaign name
 * @param {string} initialObjective - Initial campaign objective
 * @param {string} initialDescription - Initial campaign description
 */
const MitrePlaybookBuilder = ({
  mode = 'red',
  initialTechniques = [],
  onSave,
  onCancel,
  initialName = '',
  initialObjective = '',
  initialDescription = ''
}) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();

  // Campaign metadata
  const [name, setName] = useState(initialName);
  const [objective, setObjective] = useState(initialObjective);
  const [description, setDescription] = useState(initialDescription);

  // Selected techniques (left panel)
  const [selected, setSelected] = useState(initialTechniques);
  const [expandedSelected, setExpandedSelected] = useState(null);
  const [notes, setNotes] = useState(() => {
    const n = {};
    initialTechniques.forEach(t => { if (t.notes) n[t.id] = t.notes; });
    return n;
  });

  // MITRE tree (right panel)
  const [techniqueTree, setTechniqueTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTactics, setExpandedTactics] = useState({});
  const [expandedTechniques, setExpandedTechniques] = useState({});

  // Preview panel (bottom of right panel)
  const [previewTechnique, setPreviewTechnique] = useState(null);
  const [previewTactic, setPreviewTactic] = useState(null);

  // Drag state
  const [dragSource, setDragSource] = useState(null);
  const [dragData, setDragData] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dropHighlight, setDropHighlight] = useState(false);

  // Atomic tests cache (red team mode)
  const [atomicCounts, setAtomicCounts] = useState({});

  // Load MITRE tree
  useEffect(() => {
    const load = async () => {
      try {
        const tree = await mitreService.getTechniqueTree();
        setTechniqueTree(tree);
        if (tree.length > 0) {
          setExpandedTactics({ [tree[0].name]: true });
        }
      } catch (err) {
        console.error('Failed to load MITRE tree:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filter tree based on search
  const filteredTree = useMemo(() => {
    if (!searchTerm.trim()) return techniqueTree;
    const term = searchTerm.toLowerCase();
    return techniqueTree.map(tactic => {
      const filteredTechniques = tactic.techniques.filter(tech => {
        const matchesTech = tech.id.toLowerCase().includes(term) ||
          tech.name.toLowerCase().includes(term) ||
          (tech.description && tech.description.toLowerCase().includes(term));
        const matchesSub = tech.subTechniques?.some(sub =>
          sub.id.toLowerCase().includes(term) || sub.name.toLowerCase().includes(term)
        );
        return matchesTech || matchesSub;
      }).map(tech => ({
        ...tech,
        subTechniques: tech.subTechniques?.filter(sub =>
          sub.id.toLowerCase().includes(term) ||
          sub.name.toLowerCase().includes(term) ||
          tech.id.toLowerCase().includes(term) ||
          tech.name.toLowerCase().includes(term)
        )
      }));
      return { ...tactic, techniques: filteredTechniques, count: filteredTechniques.length };
    }).filter(t => t.techniques.length > 0);
  }, [techniqueTree, searchTerm]);

  // Auto-expand on search
  useEffect(() => {
    if (searchTerm.trim()) {
      const allTactics = {};
      const allTechniques = {};
      filteredTree.forEach(t => {
        allTactics[t.name] = true;
        t.techniques.forEach(tech => {
          if (tech.subTechniques?.length > 0) allTechniques[tech.id] = true;
        });
      });
      setExpandedTactics(allTactics);
      setExpandedTechniques(allTechniques);
    }
  }, [searchTerm, filteredTree]);

  // Check if technique is selected
  const isSelected = useCallback((techId) => selected.some(s => s.id === techId), [selected]);

  // Add technique
  const addTechnique = useCallback((tech, tactic) => {
    if (isSelected(tech.id)) return;
    setSelected(prev => [...prev, {
      id: tech.id,
      name: tech.name,
      tactic: tactic?.name || tech.tactic || '',
      description: tech.description || '',
      platforms: tech.platforms || [],
      url: tech.url || ''
    }]);
  }, [isSelected]);

  // Remove technique
  const removeTechnique = useCallback((techId) => {
    setSelected(prev => prev.filter(t => t.id !== techId));
    if (expandedSelected === techId) setExpandedSelected(null);
  }, [expandedSelected]);

  // Clear all
  const clearAll = () => {
    setSelected([]);
    setNotes({});
    setExpandedSelected(null);
  };

  // === Drag & Drop Handlers ===

  const handleTreeDragStart = (e, tech, tactic) => {
    if (isSelected(tech.id)) return;
    setDragSource('tree');
    setDragData({ tech, tactic });
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', tech.id);
    // Custom drag image
    if (e.target.closest) {
      const el = e.target.closest('[data-technique]');
      if (el) e.dataTransfer.setDragImage(el, 20, 20);
    }
  };

  const handleSelectedDragStart = (e, index) => {
    setDragSource('selected');
    setDragData({ index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDropZoneDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragSource === 'tree' ? 'copy' : 'move';
    setDropHighlight(true);
  };

  const handleItemDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = dragSource === 'tree' ? 'copy' : 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
      setDropHighlight(false);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    setDropHighlight(false);

    if (dragSource === 'tree' && dragData) {
      // Add from tree
      if (!isSelected(dragData.tech.id)) {
        const newTech = {
          id: dragData.tech.id,
          name: dragData.tech.name,
          tactic: dragData.tactic?.name || dragData.tech.tactic || '',
          description: dragData.tech.description || '',
          platforms: dragData.tech.platforms || [],
          url: dragData.tech.url || ''
        };
        if (dropIndex !== undefined) {
          setSelected(prev => {
            const items = [...prev];
            items.splice(dropIndex, 0, newTech);
            return items;
          });
        } else {
          setSelected(prev => [...prev, newTech]);
        }
      }
    } else if (dragSource === 'selected' && dragData) {
      const fromIndex = dragData.index;
      const toIndex = dropIndex !== undefined ? dropIndex : selected.length - 1;
      if (fromIndex !== toIndex) {
        setSelected(prev => {
          const items = [...prev];
          const [moved] = items.splice(fromIndex, 1);
          items.splice(toIndex, 0, moved);
          return items;
        });
      }
    }

    setDragSource(null);
    setDragData(null);
  };

  const handleDragEnd = () => {
    setDragSource(null);
    setDragData(null);
    setDragOverIndex(null);
    setDropHighlight(false);
  };

  // === Save ===
  const handleSave = () => {
    if (!name.trim() || !objective.trim()) return;
    onSave({
      name: name.trim(),
      objective: objective.trim(),
      description: description.trim(),
      techniques: selected.map(t => ({
        ...t,
        notes: notes[t.id] || t.notes || ''
      }))
    });
  };

  // === Style helpers ===
  const bg = darkMode ? 'bg-gray-800' : 'bg-white';
  const bgAlt = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const hover = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-96 ${bg} rounded-lg`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={textMuted}>Loading MITRE ATT&CK framework...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${text}`}>
      {/* Header */}
      <div className={`${bg} border-b ${border} p-4`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {onCancel && (
              <button onClick={onCancel} className={`p-2 rounded ${hover} ${textMuted}`}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            )}
            <div>
              <h2 className={`text-xl font-bold ${text}`}>
                {mode === 'blue' ? (t('campaigns.assessmentPlanner') || 'Assessment Planner') : (t('campaigns.campaignBuilder') || 'Campaign Builder')}
              </h2>
              <p className={`text-sm ${textMuted}`}>
                {mode === 'blue'
                  ? (t('campaigns.assessmentPlannerDesc') || 'Select MITRE techniques to define what should be tested')
                  : (t('campaigns.campaignBuilderDesc') || 'Build attack campaigns by selecting MITRE techniques')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm px-2 py-1 rounded ${
              mode === 'blue'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
            }`}>
              {mode === 'blue' ? 'Blue Team' : 'Red Team'}
            </span>
            <button
              onClick={handleSave}
              disabled={!name.trim() || !objective.trim() || selected.length === 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSave} />
              {t('common.save') || 'Save'}
            </button>
          </div>
        </div>

        {/* Campaign metadata */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('campaigns.campaignName') || 'Campaign Name'} *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="e.g., Initial Access Assessment"
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('campaigns.objective') || 'Objective'} *</label>
            <input
              type="text"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="e.g., Test phishing resilience and initial compromise paths"
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('common.description') || 'Description'}</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="Optional description..."
            />
          </div>
        </div>
      </div>

      {/* Main content - two panels */}
      <div className="flex-1 flex overflow-hidden" style={{ minHeight: '60vh' }}>
        {/* === LEFT PANEL: Selected Techniques === */}
        <div
          className={`w-2/5 border-r ${border} flex flex-col ${
            dropHighlight && dragSource === 'tree' ? (darkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''
          }`}
          onDragOver={handleDropZoneDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e)}
        >
          {/* Left panel header */}
          <div className={`p-3 border-b ${border} flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faListOl} className={textMuted} />
              <span className={`font-medium text-sm ${text}`}>
                Selected ({selected.length})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  const newStep = {
                    id: 'custom-' + Date.now(),
                    name: '',
                    isCustom: true,
                    description: '',
                    tactic: '',
                    notes: ''
                  };
                  setSelected(prev => [...prev, newStep]);
                  setExpandedSelected(newStep.id);
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${darkMode ? 'bg-indigo-700 hover:bg-indigo-600 text-indigo-100' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'}`}
                title={t('campaigns.addCustomStep') || 'Add Custom Step'}
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                {t('campaigns.addCustomStep') || 'Add Step'}
              </button>
              {selected.length > 0 && (
                <button
                  onClick={clearAll}
                  className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'}`}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Selected items list */}
          <div className="flex-1 overflow-y-auto p-2">
            {selected.length === 0 ? (
              <div className={`flex flex-col items-center justify-center h-full ${textMuted}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <FontAwesomeIcon icon={faPlus} className="text-2xl opacity-50" />
                </div>
                <p className="text-sm font-medium">{t('campaigns.noTechniquesSelected') || 'No techniques selected'}</p>
                <p className="text-xs mt-1 text-center px-4">
                  {t('campaigns.dragToAdd') || 'Drag techniques from the MITRE tree or click the + button to add them'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {selected.map((tech, index) => (
                  <div key={tech.id}>
                    {/* Drop indicator line */}
                    {dragOverIndex === index && dragSource !== null && (
                      <div className="h-0.5 bg-blue-500 rounded mx-2 my-1" />
                    )}
                    <div
                      draggable
                      onDragStart={(e) => handleSelectedDragStart(e, index)}
                      onDragOver={(e) => handleItemDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`group rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                        expandedSelected === tech.id
                          ? darkMode ? 'border-blue-600 bg-gray-700/50' : 'border-blue-400 bg-blue-50/50'
                          : darkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Main row */}
                      <div
                        className={`flex items-center gap-2 p-2 ${hover} rounded-lg`}
                        onClick={() => setExpandedSelected(expandedSelected === tech.id ? null : tech.id)}
                      >
                        <FontAwesomeIcon icon={faGripVertical} className={`${textMuted} opacity-0 group-hover:opacity-100 transition-opacity text-xs`} />
                        <span className={`text-xs font-bold ${textMuted} w-5 text-center`}>{index + 1}</span>
                        {tech.isCustom ? (
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                            darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {t('campaigns.customStep') || 'Custom'}
                          </span>
                        ) : (
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                            darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                          }`}>
                            {tech.id}
                          </span>
                        )}
                        <span className={`text-sm truncate flex-1 ${text} ${tech.isCustom && !tech.name ? 'italic opacity-50' : ''}`}>
                          {tech.name || (tech.isCustom ? (t('campaigns.addCustomStep') || 'Untitled step') : tech.name)}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          {notes[tech.id] && (
                            <FontAwesomeIcon icon={faStickyNote} className="text-yellow-500 text-xs" />
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); removeTechnique(tech.id); }}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                            title="Remove"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-xs" />
                          </button>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {expandedSelected === tech.id && (
                        <div className={`px-3 pb-3 pt-1 border-t ${border}`}>
                          {tech.isCustom ? (
                            <>
                              {/* Custom step: editable title */}
                              <div className="mb-2">
                                <label className={`text-xs font-medium ${textMuted} flex items-center gap-1 mb-1`}>
                                  <FontAwesomeIcon icon={faPen} />
                                  {t('common.title') || 'Title'}
                                </label>
                                <input
                                  type="text"
                                  value={tech.name}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setSelected(prev => prev.map(s => s.id === tech.id ? { ...s, name: val } : s));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`w-full px-2 py-1.5 rounded border text-sm ${
                                    darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                                  }`}
                                  placeholder="Step title..."
                                  autoFocus
                                />
                              </div>

                              {/* Custom step: editable description */}
                              <div className="mb-2">
                                <label className={`text-xs font-medium ${textMuted} flex items-center gap-1 mb-1`}>
                                  <FontAwesomeIcon icon={faInfoCircle} />
                                  {t('common.description') || 'Description'}
                                </label>
                                <textarea
                                  value={tech.description || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setSelected(prev => prev.map(s => s.id === tech.id ? { ...s, description: val } : s));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`w-full px-2 py-1.5 rounded border text-xs ${
                                    darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                                  }`}
                                  rows="3"
                                  placeholder="Describe what this step involves..."
                                />
                              </div>

                              {/* Custom step: notes */}
                              <div>
                                <label className={`text-xs font-medium ${textMuted} flex items-center gap-1 mb-1`}>
                                  <FontAwesomeIcon icon={faStickyNote} />
                                  {mode === 'blue' ? 'Testing instructions' : 'Notes'}
                                </label>
                                <textarea
                                  value={notes[tech.id] || ''}
                                  onChange={(e) => setNotes(prev => ({ ...prev, [tech.id]: e.target.value }))}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`w-full px-2 py-1.5 rounded border text-xs ${
                                    darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                                  }`}
                                  rows="2"
                                  placeholder={mode === 'blue'
                                    ? 'Describe what specifically should be tested...'
                                    : 'Add execution notes...'}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded text-white ${tacticColors[tech.tactic] || 'bg-gray-500'}`}>
                                  {tech.tactic}
                                </span>
                                {tech.platforms?.length > 0 && tech.platforms.map(p => (
                                  <span key={p} className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                    {p}
                                  </span>
                                ))}
                                {tech.url && (
                                  <a href={tech.url} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-600 text-xs">
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                  </a>
                                )}
                              </div>

                              {/* Description (both modes) */}
                              {tech.description && (
                                <p className={`text-xs ${textMuted} mb-2 line-clamp-3`}>
                                  {tech.description}
                                </p>
                              )}

                              {/* Notes field (blue team planning) */}
                              <div className="mt-2">
                                <label className={`text-xs font-medium ${textMuted} flex items-center gap-1 mb-1`}>
                                  <FontAwesomeIcon icon={faStickyNote} />
                                  {mode === 'blue' ? 'Testing instructions' : 'Notes'}
                                </label>
                                <textarea
                                  value={notes[tech.id] || ''}
                                  onChange={(e) => setNotes(prev => ({ ...prev, [tech.id]: e.target.value }))}
                                  className={`w-full px-2 py-1.5 rounded border text-xs ${
                                    darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                                  }`}
                                  rows="2"
                                  placeholder={mode === 'blue'
                                    ? 'Describe what specifically should be tested...'
                                    : 'Add execution notes...'}
                                />
                              </div>

                              {/* Red team: atomic test info */}
                              {mode === 'red' && (
                                <div className={`mt-2 text-xs ${textMuted}`}>
                                  <div className="flex items-center gap-1">
                                    <FontAwesomeIcon icon={faAtom} />
                                    <span>Atomic Red Team tests available for this technique</span>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Final drop indicator */}
                {dragOverIndex === selected.length && dragSource !== null && (
                  <div className="h-0.5 bg-blue-500 rounded mx-2 my-1" />
                )}
              </div>
            )}
          </div>

          {/* Tactic coverage summary */}
          {selected.length > 0 && (
            <div className={`p-3 border-t ${border}`}>
              <p className={`text-xs font-medium mb-2 ${textMuted}`}>{t('campaigns.tacticCoverage') || 'Tactic Coverage'}</p>
              <div className="flex flex-wrap gap-1">
                {(() => {
                  const tactics = {};
                  selected.filter(t => !t.isCustom).forEach(t => {
                    if (t.tactic) tactics[t.tactic] = (tactics[t.tactic] || 0) + 1;
                  });
                  return Object.entries(tactics).map(([tactic, count]) => (
                    <span key={tactic} className={`text-xs px-1.5 py-0.5 rounded text-white ${tacticColors[tactic] || 'bg-gray-500'} inline-flex items-center gap-1`}>
                      {tacticIconMap[tactic] && <FontAwesomeIcon icon={tacticIconMap[tactic]} className="text-xs" />} {tactic} ({count})
                    </span>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* === RIGHT PANEL: MITRE ATT&CK Tree === */}
        <div className="w-3/5 flex flex-col">
          {/* Search */}
          <div className={`p-3 border-b ${border}`}>
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textMuted}`} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('campaigns.searchTechniques') || "Search techniques by ID, name, or description..."}
                className={`w-full pl-10 pr-8 py-2 rounded-lg border text-sm ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'
                }`}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className={`absolute right-3 top-1/2 -translate-y-1/2 ${textMuted}`}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>

          {/* Tree + Preview split */}
          <div className="flex-1 flex overflow-hidden">
            {/* Tree */}
            <div className={`${previewTechnique ? 'w-1/2' : 'w-full'} overflow-y-auto`}>
              {filteredTree.length === 0 ? (
                <div className={`p-4 text-center ${textMuted}`}>
                  {t('common.noResults') || 'No results found'}
                </div>
              ) : (
                filteredTree.map((tactic) => (
                  <div key={tactic.name} className={`border-b ${border}`}>
                    {/* Tactic header */}
                    <button
                      onClick={() => setExpandedTactics(prev => ({ ...prev, [tactic.name]: !prev[tactic.name] }))}
                      className={`w-full flex items-center justify-between p-3 ${hover} transition-colors`}
                    >
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={expandedTactics[tactic.name] ? faChevronDown : faChevronRight}
                          className={`w-3 ${textMuted}`}
                        />
                        {tacticIconMap[tactic.name] && <FontAwesomeIcon icon={tacticIconMap[tactic.name]} className={`text-sm ${textMuted}`} />}
                        <span className={`font-medium text-sm ${text}`}>{tactic.name}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                        {tactic.count}
                      </span>
                    </button>

                    {/* Techniques */}
                    {expandedTactics[tactic.name] && (
                      <div className="pl-4 pb-1">
                        {tactic.techniques.map((tech) => {
                          const alreadySelected = isSelected(tech.id);
                          return (
                            <div key={tech.id}>
                              <div
                                data-technique={tech.id}
                                draggable={!alreadySelected}
                                onDragStart={(e) => handleTreeDragStart(e, tech, tactic)}
                                onDragEnd={handleDragEnd}
                                className={`flex items-center gap-1 group ${
                                  alreadySelected ? 'opacity-50' : 'cursor-grab active:cursor-grabbing'
                                }`}
                              >
                                {/* Expand sub-techniques */}
                                {tech.subTechniques?.length > 0 ? (
                                  <button
                                    onClick={() => setExpandedTechniques(prev => ({ ...prev, [tech.id]: !prev[tech.id] }))}
                                    className={`p-1.5 ${textMuted}`}
                                  >
                                    <FontAwesomeIcon icon={expandedTechniques[tech.id] ? faChevronDown : faChevronRight} className="w-3 text-xs" />
                                  </button>
                                ) : (
                                  <div className="w-6" />
                                )}

                                {/* Technique row */}
                                <div
                                  onClick={() => { setPreviewTechnique(tech); setPreviewTactic(tactic); }}
                                  className={`flex-1 flex items-center gap-2 py-1.5 px-2 rounded ${
                                    previewTechnique?.id === tech.id
                                      ? darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-300'
                                      : hover
                                  } transition-colors cursor-pointer`}
                                >
                                  <span className={`font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                    darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {tech.id}
                                  </span>
                                  <span className={`text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                    {tech.name}
                                  </span>
                                  {tech.subTechniques?.length > 0 && (
                                    <span className={`text-xs ${textMuted} flex-shrink-0`}>+{tech.subTechniques.length}</span>
                                  )}
                                </div>

                                {/* Add/Check button */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); if (!alreadySelected) addTechnique(tech, tactic); }}
                                  className={`p-1.5 rounded flex-shrink-0 transition-opacity ${
                                    alreadySelected
                                      ? 'text-green-500'
                                      : `opacity-0 group-hover:opacity-100 ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`
                                  }`}
                                  title={alreadySelected ? 'Already selected' : 'Add to selection'}
                                >
                                  <FontAwesomeIcon icon={alreadySelected ? faCheck : faPlus} className="text-xs" />
                                </button>
                              </div>

                              {/* Sub-techniques */}
                              {expandedTechniques[tech.id] && tech.subTechniques?.length > 0 && (
                                <div className={`pl-8 ml-3 border-l-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'} mb-1`}>
                                  {tech.subTechniques.map((subTech) => {
                                    const subSelected = isSelected(subTech.id);
                                    return (
                                      <div
                                        key={subTech.id}
                                        data-technique={subTech.id}
                                        draggable={!subSelected}
                                        onDragStart={(e) => handleTreeDragStart(e, subTech, tactic)}
                                        onDragEnd={handleDragEnd}
                                        className={`flex items-center gap-2 group ${subSelected ? 'opacity-50' : 'cursor-grab active:cursor-grabbing'}`}
                                      >
                                        <div
                                          onClick={() => { setPreviewTechnique(subTech); setPreviewTactic(tactic); }}
                                          className={`flex-1 flex items-center gap-2 py-1.5 px-2 rounded ${
                                            previewTechnique?.id === subTech.id
                                              ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                                              : hover
                                          } transition-colors cursor-pointer`}
                                        >
                                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                            darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                                          }`}>
                                            {subTech.id}
                                          </span>
                                          <span className={`text-sm truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {subTech.name}
                                          </span>
                                        </div>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); if (!subSelected) addTechnique(subTech, tactic); }}
                                          className={`p-1.5 rounded flex-shrink-0 transition-opacity ${
                                            subSelected
                                              ? 'text-green-500'
                                              : `opacity-0 group-hover:opacity-100 ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`
                                          }`}
                                        >
                                          <FontAwesomeIcon icon={subSelected ? faCheck : faPlus} className="text-xs" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Preview panel (right side of right panel) */}
            {previewTechnique && (
              <div className={`w-1/2 border-l ${border} flex flex-col overflow-y-auto`}>
                <div className={`p-4 border-b ${border}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className={`font-mono text-sm px-2 py-1 rounded mr-2 ${
                        darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                      }`}>
                        {previewTechnique.id}
                      </span>
                      <h3 className={`text-lg font-bold mt-2 ${text}`}>{previewTechnique.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded text-white ${tacticColors[previewTactic?.name] || 'bg-gray-500'}`}>
                          {previewTactic?.name}
                        </span>
                        {previewTechnique.url && (
                          <a href={previewTechnique.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-sm">
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </a>
                        )}
                      </div>
                    </div>
                    <button onClick={() => setPreviewTechnique(null)} className={`p-1 rounded ${hover} ${textMuted}`}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 p-4">
                  <h4 className={`text-sm font-medium mb-2 ${textMuted}`}>Description</h4>
                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {previewTechnique.description || 'No description available.'}
                  </p>

                  {previewTechnique.platforms?.length > 0 && (
                    <div className="mt-4">
                      <h4 className={`text-sm font-medium mb-2 ${textMuted}`}>Platforms</h4>
                      <div className="flex flex-wrap gap-1">
                        {previewTechnique.platforms.map((p, i) => (
                          <span key={i} className={`px-2 py-1 text-xs rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Red team: show atomic test info */}
                  {mode === 'red' && (
                    <div className="mt-4">
                      <h4 className={`text-sm font-medium mb-2 ${textMuted}`}>
                        <FontAwesomeIcon icon={faAtom} className="mr-1" />
                        Atomic Red Team Tests
                      </h4>
                      <p className={`text-xs ${textMuted}`}>
                        Click "Add" then view technique details in the selected panel to see execution options.
                      </p>
                    </div>
                  )}
                </div>

                {/* Add button at bottom of preview */}
                <div className={`p-4 border-t ${border}`}>
                  {isSelected(previewTechnique.id) ? (
                    <button disabled className={`w-full py-2 rounded-lg text-sm ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                      <FontAwesomeIcon icon={faCheck} className="mr-2" />
                      Already Selected
                    </button>
                  ) : (
                    <button
                      onClick={() => addTechnique(previewTechnique, previewTactic)}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Add {previewTechnique.id} - {previewTechnique.name}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MitrePlaybookBuilder;
